import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-0.5" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "flex flex-col gap-6 min-h-[60dvh]" };
const _hoisted_5 = { class: "w-full md:w-102" };
import { computed, ref, watch, useId } from "vue";
import { useI18n } from "vue-i18n";
import { AutoComplete, Button, Modal } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { useOrganisationSearch } from "@/modules/organisation/public";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { isoToDisplayDate, displayDateToIso } from "@/shared/utils/date-formatting";
import { createContactRelation } from "../../infrastructure/api/contactRelationsApi";
import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import CreateOrganisationModal from "@/modules/organisation/presentation/components/CreateOrganisationModal.vue";
import RelationFunctionRows from "./RelationFunctionRows.vue";
// ─── Props / emits ─────────────────────────────────────────────────────────────
export default /*@__PURE__*/ _defineComponent({
    __name: 'CreateRelationModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ──────────────────────────────────────────────────────────────
        const { locale } = useI18n();
        const { canEditOphub: canEdit } = usePermissions();
        const profileStore = useProfileStore();
        const { toastSuccess, toastError } = useAppToast();
        const titleId = useId();
        const descriptionId = useId();
        // ─── Organisation search ───────────────────────────────────────────────────────
        const { suggestions: orgSuggestions, isLoading: orgLoading, isError: orgError, search: orgSearch, clear: orgClear } = useOrganisationSearch({ extended: true });
        const selectedOrgId = ref("");
        // ─── Function rows ─────────────────────────────────────────────────────────────
        function emptyRow() {
            const todayIso = new Date().toISOString().slice(0, 10);
            return {
                _id: crypto.randomUUID(),
                title: "",
                function: "",
                start: isoToDisplayDate(todayIso, profileStore.profile?.dateFormat, locale.value),
                end: "",
            };
        }
        const functionRows = ref([emptyRow()]);
        const functionRowsRef = ref();
        // ─── Save gating ───────────────────────────────────────────────────────────────
        const canSave = computed(() => canEdit.value &&
            !!selectedOrgId.value &&
            functionRows.value.length > 0 &&
            functionRows.value.every((r) => !!r.function) &&
            !(functionRowsRef.value?.hasErrors ?? false));
        // ─── Nested: Create new organisation ──────────────────────────────────────────
        const showCreateOrg = ref(false);
        const createOrgMounted = ref(false);
        watch(showCreateOrg, (v) => {
            if (v)
                createOrgMounted.value = true;
        });
        function openCreateOrg() {
            showCreateOrg.value = true;
        }
        async function onOrgCreated(newId, name) {
            showCreateOrg.value = false;
            selectedOrgId.value = newId;
            // Inject the new org as an option so the AutoComplete can display the name
            orgSuggestions.value = [{ value: newId, label: name || newId }];
        }
        // ─── Submit ────────────────────────────────────────────────────────────────────
        const saving = ref(false);
        // Backend (OPHUB) validation error codes the user can recover from → localized i18n keys.
        // Unmapped codes and unexpected errors fall back to the generic create-error toast.
        const RELATION_ERROR_KEYS = {
            "Relation.Organisation.Required": "contact.detail.relations.errors.organisationRequired",
            "Relation.Function.InvalidDateRange": "contact.detail.relations.errors.invalidDateRange",
            "Relation.Function.Required": "contact.detail.relations.errors.functionRequired",
            "Relation.Function.Invalid": "contact.detail.relations.errors.functionInvalid",
            "Relation.Function.StartDateRequired": "contact.detail.relations.errors.functionStartDateRequired",
            "Relation.Function.TitleTooLong": "contact.detail.relations.errors.functionTitleTooLong",
        };
        async function handleSubmit() {
            if (!canSave.value)
                return;
            saving.value = true;
            try {
                await createContactRelation(props.contactId, {
                    organisationId: selectedOrgId.value,
                    functions: functionRows.value.map((row) => ({
                        title: row.title.trim() || null,
                        function: row.function,
                        startDate: displayDateToIso(row.start, profileStore.profile?.dateFormat, locale.value) ||
                            new Date().toISOString().slice(0, 10),
                        endDate: row.end
                            ? displayDateToIso(row.end, profileStore.profile?.dateFormat, locale.value) || null
                            : null,
                    })),
                });
                toastSuccess("contact.detail.relations.create.success");
                emit("created");
                open.value = false;
                resetForm();
            }
            catch (err) {
                const code = getApiErrorCode(err);
                const mappedKey = code ? RELATION_ERROR_KEYS[code] : undefined;
                if (!mappedKey) {
                    // Unexpected error (network, 500, unmapped code) — keep a diagnostic trail.
                    logger.error("CreateRelationModal: save failed", err);
                }
                toastError(mappedKey ?? "contact.detail.relations.create.error");
            }
            finally {
                saving.value = false;
            }
        }
        // ─── Cancel / reset ────────────────────────────────────────────────────────────
        function resetForm() {
            selectedOrgId.value = "";
            functionRows.value = [emptyRow()];
            orgClear();
        }
        function handleCancel() {
            open.value = false;
            resetForm();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    modelValue: open.value,
                    "onUpdate:modelValue": [
                        _cache[3] || (_cache[3] = ($event) => ((open).value = $event)),
                        _cache[4] || (_cache[4] = (v) => !v && handleCancel())
                    ],
                    size: "xl",
                    "header-position": "center",
                    "footer-position": "split",
                    "show-close-button": true,
                    "show-header-border": false,
                    "show-footer-border": false,
                    "aria-labelledby": _unref(titleId),
                    "aria-describedby": _unref(descriptionId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-(length:--size-xxl) font-(--weight-bold)"
                            }, _toDisplayString(_ctx.$t("contact.detail.relations.create.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                            _createElementVNode("p", {
                                id: _unref(descriptionId),
                                class: "text-(length:--size-l) text-(--text-text-body-subtle)"
                            }, _toDisplayString(_ctx.$t("contact.detail.relations.create.subtitle")), 9 /* TEXT, PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('common.cancel'),
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _ctx.$t('common.save'),
                            loading: saving.value,
                            disabled: !canSave.value,
                            onClick: handleSubmit
                        }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_ctx.$t), {
                                    modelValue: selectedOrgId.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((selectedOrgId).value = $event)),
                                    label: _ctx.$t('contact.detail.relations.create.fields.organisation.label'),
                                    placeholder: _ctx.$t('contact.detail.relations.create.fields.organisation.placeholder'),
                                    "empty-label": _ctx.$t('contact.detail.relations.create.fields.organisation.empty'),
                                    options: _unref(orgSuggestions),
                                    loading: _unref(orgLoading),
                                    error: _unref(orgError) ? _ctx.$t('contact.detail.relations.create.fields.organisation.searchError') : undefined,
                                    required: true,
                                    disabled: !_unref(canEdit),
                                    "icon-left": "ri-building-line",
                                    "icon-right": "ri-search-line",
                                    "full-width": "",
                                    "create-label": _ctx.$t('common.createNewOrganisation'),
                                    onSearch: _unref(orgSearch),
                                    onClear: _unref(orgClear),
                                    onCreate: _cache[1] || (_cache[1] = ($event) => (openCreateOrg()))
                                }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "empty-label", "options", "loading", "error", "disabled", "create-label", "onSearch", "onClear"])
                            ]),
                            _createVNode(RelationFunctionRows, {
                                ref_key: "functionRowsRef",
                                ref: functionRowsRef,
                                modelValue: functionRows.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((functionRows).value = $event)),
                                "require-title": true
                            }, null, 8 /* PROPS */, ["modelValue"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]),
                (createOrgMounted.value)
                    ? (_openBlock(), _createBlock(CreateOrganisationModal, {
                        key: 0,
                        modelValue: showCreateOrg.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showCreateOrg).value = $event)),
                        "redirect-on-create": false,
                        onCreated: onOrgCreated
                    }, null, 8 /* PROPS */, ["modelValue"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
